import React, { useEffect } from 'react'
import { ActivityIndicator, View, ViewStyle } from 'react-native'
import { observer } from 'mobx-react-lite'
import { useHistory } from 'react-router-dom'
import { Stack, useMediaQuery } from '@mui/material'

import { useStores, setPageTitle, useQuery } from '@trivie/core'
import { Text } from '@trivie/ui-web/components/text'
import { QuizItemList } from '@trivie/ui-mobile/components/quiz/QuizItemList'
import { Exit } from '@trivie/ui-mobile/components/quiz/Exit'
import { Confirmation } from './confirmation'

/**
 * Thin survey play shell — mirrors game-screen.tsx. All rendering/progression is
 * delegated to the shared QuizItemList driven by the SurveyPlay model (mode="survey").
 * Surveys have no countdown and no background music.
 */
export const SurveyScreen = observer(() => {
  const { surveyStore, userStore, uiStore } = useStores()
  const { branding } = uiStore
  const { user } = userStore
  const { startSurvey, play } = surveyStore
  const history = useHistory()
  const isMobile = useMediaQuery('(max-width:800px)')
  const { text_color, quiz_background } = branding

  const query = useQuery()
  const metadata = {
    assignment_id: Number(query.get('assignment_id')) || null,
    subscription_user_id: Number(query.get('subscription_user_id')) || null,
    path_id: Number(query.get('path_id')) || null,
    content_user_id: Number(query.get('content_user_id')) || null,
    survey_id: Number(query.get('survey_id')) || null,
  }

  useEffect(() => {
    if (user) {
      startSurvey(metadata.survey_id as number, user.id, metadata).then((resp: any) => {
        // Already responded or failed to start — return to where we came from.
        if (surveyStore.status === 'error' || resp?.responded) {
          history.goBack()
        }
      })
    }
    setPageTitle(`Survey - ${process.env.REACT_APP_SITE_TITLE}`)
  }, [])

  const exit = () => history.goBack()

  if (play?.completed) {
    return <Confirmation title={play?.title ?? ''} />
  }

  if (!play || surveyStore.isLoading) {
    return (
      <View style={{ ...CONTAINER, backgroundColor: quiz_background }}>
        <View style={{ flex: 1, justifyContent: 'center' }}>
          <ActivityIndicator />
        </View>
      </View>
    )
  }

  const titleStyle = { fontSize: isMobile ? 18 : 24, color: text_color }

  return (
    <View style={{ ...CONTAINER, backgroundColor: quiz_background }}>
      <Stack gap={2} padding={2} alignItems="center" direction="row" justifyContent="space-between">
        <Exit color={text_color} onPress={exit} />
        <Text noWrap text={play.title} variant="headline" style={titleStyle} />
        {/* Spacer to keep the title centered (no music toggle for surveys). */}
        <View style={{ width: 40 }} />
      </Stack>
      <QuizItemList quiz={play} branding={branding} mode="survey" />
    </View>
  )
})

const CONTAINER: ViewStyle = {
  position: 'absolute',
  height: '100%',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 998,
}
